import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  TextField, 
  Typography, 
  Paper, 
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PublishIcon from '@mui/icons-material/Publish';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ScheduleIcon from '@mui/icons-material/Schedule';

// Utility function to capitalize first letter of each word
const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

export default function EditArticle({ articleId, articleData, onBack, isReadOnly = false, userRole = "Admin" }) {
  console.log('🎯 EditArticle component rendered with:', { articleId, articleData, isReadOnly, userRole });
  console.log('🔍 UserRole check for Publish button:', { 
    userRole, 
    isAdmin: userRole === 'Admin',
    isApprover: userRole === 'Approver',
    postStatus: articleData?.postStatus 
  });
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [video, setVideo] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [fetchingCategories, setFetchingCategories] = useState(true);
  const [loadingArticle, setLoadingArticle] = useState(true);
  
  // New form fields for news type and location
  const [newsType, setNewsType] = useState(1); // 1: National, 2: State, 3: District, 4: Constituency
  const [selectedState, setSelectedState] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedConstituency, setSelectedConstituency] = useState('');
  const [isBreakingNews, setIsBreakingNews] = useState(false);
  
  // Push notification fields
  const [sendNotification, setSendNotification] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  
  // Position number for publishing
  const [publishPositionNo, setPublishPositionNo] = useState('');
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  
  // Schedule dialog state
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleTime, setScheduleTime] = useState('');
  const [expiryTime, setExpiryTime] = useState('');
  
  // States, districts, and constituencies data
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [fetchingStates, setFetchingStates] = useState(true);
  const [fetchingDistricts, setFetchingDistricts] = useState(false);
  const [fetchingConstituencies, setFetchingConstituencies] = useState(false);
  
  // Form fields for API
  const [postType, setPostType] = useState(articleData?.postType || 1);
  const [languageId] = useState('L100');
  const [latitude] = useState(73.90);
  const [longitude] = useState(78.90);

  // Get session data from localStorage
  const getSessionData = () => {
    const sessionId = localStorage.getItem('sessionId');
    const dashAdminId = localStorage.getItem('dashAdminId');
    
    console.log('🔍 Session data check for EditArticle:', {
      sessionId: sessionId ? 'Found' : 'Not found',
      dashAdminId: dashAdminId ? 'Found' : 'Not found',
      sessionIdValue: sessionId,
      dashAdminIdValue: dashAdminId
    });
    
    return { sessionId, dashAdminId };
  };

  // Load article details from passed data
  const loadArticleDetails = useCallback(() => {
    if (!articleData) {
      console.error('❌ Article data not found');
      setLoadingArticle(false);
      return;
    }

    setLoadingArticle(true);
    try {
      setTitle(articleData.title || '');
      setDescription(articleData.description || '');
      setSelectedCategory(articleData.categoryId || '');
      setNewsType(articleData.newsType || 1);
      setSelectedState(articleData.stateId || '');
      setSelectedDistrict(articleData.districtId || '');
      setSelectedConstituency(articleData.constituencyId || '');
      setIsBreakingNews(articleData.isBreakingNews || false);
      setPostType(articleData.postType || 1);
      
      // Set existing media if available
      if (articleData.mediaArray && articleData.mediaArray.length > 0) {
        const media = articleData.mediaArray[0];
        
        if (media.mediaType === 1 && media.imageUrl) {
          // Image media
          setImageUrl(media.imageUrl);
        } else if (media.mediaType === 2 && media.videoUrl) {
          // Video media
          setVideoUrl(media.videoUrl);
        }
      }
      
      // Fetch districts if state is selected (for District or Constituency news)
      if (articleData.stateId && (articleData.newsType === 3 || articleData.newsType === 4)) {
        fetchDistricts(articleData.stateId).then(() => {
          // After districts are fetched, fetch constituencies if needed
          if (articleData.districtId && articleData.newsType === 4) {
            fetchConstituencies(articleData.districtId);
          }
        }).catch((error) => {
          console.error('Error fetching districts:', error);
        });
      } else if (articleData.districtId && articleData.newsType === 4) {
        // If we have districtId but no stateId (shouldn't happen, but handle it)
        fetchConstituencies(articleData.districtId);
      }
    } catch (error) {
      console.error('Error loading article details:', error);
      setError('Failed to load article details');
    } finally {
      setLoadingArticle(false);
    }
  }, [articleData]);

  // Fetch categories from API
  const fetchCategories = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for categories');
      setFetchingCategories(false);
      return;
    }

    setFetchingCategories(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          isSearchFilter: false,
          search: "",
          sortBy: 'sNo', // Sort by sNo for ranking
          sortOrder: 'asc' // Ascending order for ranking
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Categories fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          // Sort categories by sNo for ranking
          const sortedCategories = result.data.sort((a, b) => {
            const aSNo = a.sNo || 999; // Default to high number if sNo is missing
            const bSNo = b.sNo || 999;
            return aSNo - bSNo; // Sort in ascending order
          });
          setCategories(sortedCategories);
        } else {
          setCategories([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching categories:', errorData);
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setFetchingCategories(false);
    }
  }, []);

  // Fetch states from API
  const fetchStates = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for states');
      setFetchingStates(false);
      return;
    }

    setFetchingStates(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_states', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          islanguageIdFilter: false,
          languageId: "",
          isSearchFilter: false,
          search: "",
          sortBy: 'sNo', // Sort by sNo for ranking
          sortOrder: 'asc' // Ascending order for ranking
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('States fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          // Sort states by sNo for ranking
          const sortedStates = result.data.sort((a, b) => {
            const aSNo = a.sNo || 999; // Default to high number if sNo is missing
            const bSNo = b.sNo || 999;
            return aSNo - bSNo; // Sort in ascending order
          });
          setStates(sortedStates);
        } else {
          setStates([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching states:', errorData);
        setStates([]);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    } finally {
      setFetchingStates(false);
    }
  }, []);

  // Fetch districts from API
  const fetchDistricts = useCallback(async (stateId) => {
    if (!stateId) {
      setDistricts([]);
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for districts');
      setFetchingDistricts(false);
      return;
    }

    setFetchingDistricts(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_districts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          isstateIdFilter: true,
          stateId: stateId,
          isSearchFilter: false,
          search: ""
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Districts fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          setDistricts(result.data);
        } else {
          setDistricts([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching districts:', errorData);
        setDistricts([]);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    } finally {
      setFetchingDistricts(false);
    }
  }, []);

  // Fetch constituencies from API
  const fetchConstituencies = useCallback(async (districtId) => {
    if (!districtId) {
      setConstituencies([]);
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for constituencies');
      setFetchingConstituencies(false);
      return;
    }

    setFetchingConstituencies(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_constituencies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          isstateIdFilter: false,
          stateId: "",
          isdistrictIdFilter: true,
          districtId: districtId,
          isSearchFilter: false,
          search: ""
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Constituencies fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          setConstituencies(result.data);
        } else {
          setConstituencies([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching constituencies:', errorData);
        setConstituencies([]);
      }
    } catch (error) {
      console.error('Error fetching constituencies:', error);
      setConstituencies([]);
    } finally {
      setFetchingConstituencies(false);
    }
  }, []);

  // Handle news type change
  const handleNewsTypeChange = (event) => {
    const newNewsType = parseInt(event.target.value);
    setNewsType(newNewsType);
    
    // Reset state, district, and constituency selections when news type changes
    if (newNewsType === 1) { // National
      setSelectedState('');
      setSelectedDistrict('');
      setSelectedConstituency('');
      setDistricts([]);
      setConstituencies([]);
    } else if (newNewsType === 2) { // State
      setSelectedDistrict('');
      setSelectedConstituency('');
      setDistricts([]);
      setConstituencies([]);
    } else if (newNewsType === 3) { // District
      setSelectedConstituency('');
      setConstituencies([]);
    } else if (newNewsType === 4) { // Constituency
      // Keep state and district, but reset constituency
      setSelectedConstituency('');
      setConstituencies([]);
    }
  };

  // Handle state change
  const handleStateChange = (event) => {
    const stateId = event.target.value;
    setSelectedState(stateId);
    setSelectedDistrict(''); // Reset district when state changes
    setSelectedConstituency(''); // Reset constituency when state changes
    
    if (stateId && (newsType === 3 || newsType === 4)) { // Fetch districts if news type is District or Constituency
      fetchDistricts(stateId);
    } else {
      setDistricts([]);
    }
    setConstituencies([]);
  };

  // Handle district change
  const handleDistrictChange = (event) => {
    const districtId = event.target.value;
    setSelectedDistrict(districtId);
    setSelectedConstituency(''); // Reset constituency when district changes
    
    if (districtId && newsType === 4) { // Only fetch constituencies if news type is Constituency
      fetchConstituencies(districtId);
    } else {
      setConstituencies([]);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchCategories();
    fetchStates();
    loadArticleDetails();
  }, [fetchCategories, fetchStates, loadArticleDetails]);


  // Upload image function
  const uploadImage = async (file) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      throw new Error('Session data not found');
    }

    const formData = new FormData();
    formData.append('routeType', '1');
    formData.append('file', file);
    formData.append('dashAdminId', dashAdminId);
    formData.append('sessionId', sessionId);

    console.log('📤 Uploading image with FormData:', {
      routeType: '1',
      dashAdminId,
      sessionId,
      fileName: file.name,
      fileSize: file.size
    });

    const response = await fetch('https://api.jpnewz.com/upload/upload_image', {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();
    console.log('📥 Upload image response:', result);

    if (!response.ok) {
      console.error('❌ Upload failed with status:', response.status);
      throw new Error(result.message || `Upload failed with status ${response.status}`);
    }

    if (!result.status) {
      console.error('❌ Upload failed with result:', result);
      throw new Error(result.message || 'Upload failed');
    }

    if (!result.data) {
      console.error('❌ Upload response missing data:', result);
      throw new Error('Upload response missing data');
    }

    if (!result.data._id) {
      console.error('❌ Upload response missing _id:', result.data);
      throw new Error('Upload response missing image ID');
    }

    console.log('✅ Upload successful, image ID:', result.data._id);
    return result.data;
  };

  // Upload video function
  const uploadVideo = async (file) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      throw new Error('Session data not found');
    }

    const formData = new FormData();
    formData.append('routeType', '1');
    formData.append('file', file);
    formData.append('dashAdminId', dashAdminId);
    formData.append('sessionId', sessionId);

    console.log('📤 Uploading video with FormData:', {
      routeType: '1',
      dashAdminId,
      sessionId,
      fileName: file.name,
      fileSize: file.size
    });

    try {
      const response = await fetch('https://api.jpnewz.com/upload/upload_video', {
        method: 'POST',
        body: formData,
      });

      console.log('📥 Upload video response status:', response.status);

      if (!response.ok) {
        console.error('❌ Video upload failed with status:', response.status);
        
        // Handle 413 specifically
        if (response.status === 413) {
          throw new Error(`Video file is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Please compress the video or use a smaller file.`);
        }
        
        // Try to get error message from response
        let errorMessage = `Video upload failed with status ${response.status}`;
        try {
          const errorResult = await response.json();
          errorMessage = errorResult.message || errorMessage;
        } catch (e) {
          console.log('Could not parse error response as JSON');
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('📥 Upload video response:', result);

      if (!result.status) {
        console.error('❌ Video upload failed with result:', result);
        throw new Error(result.message || 'Video upload failed');
      }

      if (!result.data) {
        console.error('❌ Video upload response missing data:', result);
        throw new Error('Video upload response missing data');
      }

      if (!result.data._id) {
        console.error('❌ Video upload response missing _id:', result.data);
        throw new Error('Video upload response missing video ID');
      }

      console.log('✅ Video upload successful, video ID:', result.data._id);
      return result.data;
      
    } catch (error) {
      console.error('❌ Video upload error:', error);
      
      // Handle network/CORS errors
      if (error.name === 'TypeError' && error.message.includes('Load failed')) {
        throw new Error(`Video upload failed. The file might be too large (${(file.size / (1024 * 1024)).toFixed(1)}MB) or there's a network issue. Please try a smaller file.`);
      }
      
      // Re-throw other errors
      throw error;
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setError('');
      
      // Create preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImageUrl('');
    setError('');
    // Reset the file input
    const fileInput = document.querySelector('input[type="file"][accept="image/*"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideo(file);
      setError('');
      
      // Create preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setVideoUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveVideo = () => {
    setVideo(null);
    setVideoUrl('');
    setError('');
    // Reset the file input
    const fileInput = document.querySelector('input[type="file"][accept="video/*"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Handle send push notification
  const handleSendNotification = async () => {
    const { sessionId, dashAdminId } = getSessionData();
    if (!sessionId || !dashAdminId) {
      setError('Session data not found. Please login again.');
      return;
    }

    // Validate notification fields - only title is required, body is hidden
    if (!notificationTitle.trim()) {
      setError('Please enter notification title');
      return;
    }
    // Body parameter is hidden - not required from user input
    // if (!notificationBody.trim()) {
    //   setError('Please enter notification body');
    //   return;
    // }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const notificationResponse = await fetch('https://api.jpnewz.com/dashboard/send_post_notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          postId: articleId,
          notificationTitle: notificationTitle.trim(),
          notificationBody: '', // Body parameter hidden - sending empty string
          excludeRoles: ['Super Admin', 'Approver'] // Exclude superadmin and approver from notifications
        }),
      });

      console.log('request body', {
        sessionId,
        dashAdminId,
        postId: articleId,
        notificationTitle: notificationTitle.trim(),
        notificationBody: '', // Body parameter hidden
        excludeRoles: ['Super Admin', 'Approver']
      })

      const notificationResult = await notificationResponse.json();
      console.log('Push notification response:', notificationResult);

      if (notificationResponse.ok && notificationResult.status === true) {
        setSuccess('Push notification sent successfully!');
        // Clear the notification fields after successful send
        setSendNotification(false);
        setNotificationTitle('');
        // setNotificationBody(''); // Body field is hidden, no need to clear
      } else {
        setError(`Failed to send notification: ${notificationResult.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      setError(`Error sending notification: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle approve post
  const handleApprove = async () => {
    const { sessionId, dashAdminId } = getSessionData();
    if (!sessionId || !dashAdminId) {
      setError('Session data not found. Please login again.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/approve_post', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          postId: articleId
        }),
      });

      const result = await response.json();
      console.log('Approve post response:', result);

      if (response.ok && result.status === true) {
        setSuccess('Post approved successfully!');
        // Optionally navigate back after a delay
        setTimeout(() => {
          if (onBack) onBack();
        }, 2000);
      } else {
        setError(`Error: ${result.message || 'Failed to approve post'}`);
      }
    } catch (error) {
      console.error('Error approving post:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle reject post
  const handleReject = async () => {
    const { sessionId, dashAdminId } = getSessionData();
    if (!sessionId || !dashAdminId) {
      setError('Session data not found. Please login again.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/reject_post', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          postId: articleId
        }),
      });

      const result = await response.json();
      console.log('Reject post response:', result);

      if (response.ok && result.status === true) {
        setSuccess('Post rejected successfully!');
        // Optionally navigate back after a delay
        setTimeout(() => {
          if (onBack) onBack();
        }, 2000);
      } else {
        setError(`Error: ${result.message || 'Failed to reject post'}`);
      }
    } catch (error) {
      console.error('Error rejecting post:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle open publish dialog
  const handleOpenPublishDialog = () => {
    console.log('🔵 Opening publish dialog - resetting position number to empty');
    setPublishPositionNo(''); // Reset to empty - user must enter value
    setError(''); // Clear any previous errors
    setPublishDialogOpen(true);
  };

  // Handle close publish dialog
  const handleClosePublishDialog = () => {
    setPublishDialogOpen(false);
    setPublishPositionNo(''); // Reset to empty
  };

  // Handle publish post (direct publish - no schedule)
  const handlePublish = async () => {
    console.log('🔵 Publish button clicked in dialog, positionNo value:', publishPositionNo, 'Type:', typeof publishPositionNo);
    
    // Validate position number - must be explicitly entered, no defaults
    const positionNum = typeof publishPositionNo === 'string' ? parseInt(publishPositionNo) : publishPositionNo;
    
    if (publishPositionNo === '' || publishPositionNo === undefined || publishPositionNo === null || 
        isNaN(positionNum) || positionNum < 0 || positionNum > 99) {
      console.log('❌ Validation failed - position number is invalid:', publishPositionNo);
      setError('Please enter a valid position number (0-99)');
      return;
    }
    
    console.log('✅ Validation passed - publishing with position number:', positionNum);

    const { sessionId, dashAdminId } = getSessionData();
    if (!sessionId || !dashAdminId) {
      setError('Session data not found. Please login again.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Direct publish - no schedule, no expiry
      const requestBody = {
        sessionId,
        dashAdminId,
        postId: articleId,
        positionNo: typeof publishPositionNo === 'string' ? parseInt(publishPositionNo) : publishPositionNo,
        isShedulePost: false,
        sheduleTime: null,
        isExpiryTime: false,
        expiryTime: null,
        excludeRoles: ['Super Admin', 'Approver'] // Exclude superadmin and approver from notifications when publishing
      };

      console.log('📤 Publishing post (direct):', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/publish_post', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log('Publish post response:', result);

      if (response.ok && result.status === true) {
        setSuccess('Post published successfully!');
        setPublishDialogOpen(false); // Close dialog on success
        // Optionally navigate back after a delay
        setTimeout(() => {
          if (onBack) onBack();
        }, 2000);
      } else {
        setError(`Error: ${result.message || 'Failed to publish post'}`);
      }
    } catch (error) {
      console.error('Error publishing post:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle open schedule dialog
  const handleOpenScheduleDialog = () => {
    console.log('🟢 Opening schedule dialog');
    setScheduleTime('');
    setExpiryTime('');
    setError('');
    setScheduleDialogOpen(true);
  };

  // Handle close schedule dialog
  const handleCloseScheduleDialog = () => {
    setScheduleDialogOpen(false);
    setScheduleTime('');
    setExpiryTime('');
  };

  // Handle schedule post
  const handleSchedule = async () => {
    console.log('🟢 Schedule button clicked, scheduleTime:', scheduleTime, 'expiryTime:', expiryTime, 'postType:', postType);
    
    // Validate schedule time
    if (!scheduleTime || scheduleTime.trim() === '') {
      setError('Please select a schedule time');
      return;
    }

    // For post type 2, validate expiry time
    if (postType === 2 && (!expiryTime || expiryTime.trim() === '')) {
      setError('Please select an expiry time for Image Post');
      return;
    }

    // Validate schedule time is in the future
    const scheduleDate = new Date(scheduleTime);
    if (scheduleDate <= new Date()) {
      setError('Schedule time must be in the future');
      return;
    }

    // For post type 2, validate expiry time is after schedule time
    if (postType === 2 && expiryTime) {
      const expiryDate = new Date(expiryTime);
      if (expiryDate <= scheduleDate) {
        setError('Expiry time must be after schedule time');
        return;
      }
    }

    const { sessionId, dashAdminId } = getSessionData();
    if (!sessionId || !dashAdminId) {
      setError('Session data not found. Please login again.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Format schedule time to ISO string
      const formattedScheduleTime = new Date(scheduleTime).toISOString();
      
      // Build request body (no positionNo for scheduled posts)
      const requestBody = {
        sessionId,
        dashAdminId,
        postId: articleId,
        isShedulePost: true,
        sheduleTime: formattedScheduleTime,
        isExpiryTime: postType === 2 ? true : false,
        expiryTime: postType === 2 && expiryTime ? new Date(expiryTime).toISOString() : null
      };

      console.log('📤 Scheduling post:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/publish_post', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log('Schedule post response:', result);

      if (response.ok && result.status === true) {
        setSuccess('Post scheduled successfully!');
        setScheduleDialogOpen(false); // Close dialog on success
        // Optionally navigate back after a delay
        setTimeout(() => {
          if (onBack) onBack();
        }, 2000);
      } else {
        setError(`Error: ${result.message || 'Failed to schedule post'}`);
      }
    } catch (error) {
      console.error('Error scheduling post:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }
    if (!description.trim()) {
      setError('Please enter a description');
      return;
    }
    if (!selectedCategory) {
      setError('Please select a category');
      return;
    }

    // Validate news type specific requirements
    if (newsType === 2 && !selectedState) { // State news requires state selection
      setError('Please select a state for state-level news');
      return;
    }
    if (newsType === 3 && !selectedState) { // District news requires state selection
      setError('Please select a state for district-level news');
      return;
    }
    if (newsType === 3 && !selectedDistrict) { // District news requires district selection
      setError('Please select a district for district-level news');
      return;
    }
    if (newsType === 4 && !selectedState) { // Constituency news requires state selection
      setError('Please select a state for constituency-level news');
      return;
    }
    if (newsType === 4 && !selectedDistrict) { // Constituency news requires district selection
      setError('Please select a district for constituency-level news');
      return;
    }
    if (newsType === 4 && !selectedConstituency) { // Constituency news requires constituency selection
      setError('Please select a constituency for constituency-level news');
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      setError('Session data not found. Please login again.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      let mediaArray = [];
      
      // Handle image upload if new image is selected
      if (image) {
        setUploadingImage(true);
        console.log('🔄 Starting image upload for file:', image.name);
        const uploadResult = await uploadImage(image);
        
        console.log('✅ Image uploaded successfully:', {
          imageId: uploadResult._id,
          imageUrl: uploadResult.s3FileURL
        });
        setUploadingImage(false);

        mediaArray = [
          {
            mediaType: 1, // 1 for image
            imageId: uploadResult._id,
            videoId: null
          }
        ];
      } else if (video) {
        // Handle video upload if new video is selected
        setUploadingVideo(true);
        console.log('🔄 Starting video upload for file:', video.name);
        const uploadResult = await uploadVideo(video);
        
        console.log('✅ Video uploaded successfully:', {
          videoId: uploadResult._id,
          videoUrl: uploadResult.s3FileURL
        });
        setUploadingVideo(false);

        mediaArray = [
          {
            mediaType: 2, // 2 for video
            imageId: null,
            videoId: uploadResult._id
          }
        ];
      } else {
        // If no new media is uploaded, preserve existing media array
        if (articleData?.mediaArray && articleData.mediaArray.length > 0) {
          // Clean the media array to only include fields the API expects
          mediaArray = articleData.mediaArray.map(media => ({
            mediaType: media.mediaType,
            imageId: media.imageId,
            videoId: media.videoId
          }));
          console.log('📋 Preserving existing media array (cleaned):', mediaArray);
        }
      }

      // Now update the post
      const requestBody = {
        sessionId,
        dashAdminId,
        postId: articleId,
        postType,
        languageId,
        newsType,
        title: title.trim(),
        description: description.trim(),
        categoryId: selectedCategory,
        stateId: newsType === 1 ? null : selectedState, // null for national, selected state for state/district/constituency
        districtId: (newsType === 3 || newsType === 4) ? selectedDistrict : null, // only set for district/constituency news
        constituencyId: newsType === 4 ? selectedConstituency : null, // only set for constituency news
        latitude,
        longitude,
        hashTags: articleData?.hashTags || '', // Use existing hashtags or empty string
        expiryTime: articleData?.expiryTime || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // Use existing expiry or 30 days from now
        colorCode: articleData?.colorCode || null,
        isBreakingNews,
        mediaArray // Always include mediaArray, even if empty
      };

      console.log('📤 Sending update post request:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/edit_post', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log('📥 Update post response:', result);

      if (response.ok && result.status === true) {
        setSuccess('Article updated successfully!');
        // Optionally navigate back after a delay
        setTimeout(() => {
          if (onBack) onBack();
        }, 2000);
      } else {
        setError(`Error: ${result.message || 'Failed to update article'}`);
      }
    } catch (error) {
      console.error('Error updating article:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setSubmitting(false);
      setUploadingImage(false);
      setUploadingVideo(false);
    }
  };

  if (loadingArticle) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading article details...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '70vh', gap: 3 }}>
      {/* Left: Form */}
      <Paper elevation={3} sx={{ flex: 1, p: 4, display: 'flex', flexDirection: 'column', gap: 3, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{isReadOnly ? 'View News Article' : 'Edit News Article'}</Typography>
          <Button variant="outlined" onClick={onBack}>
            Back to News List
          </Button>
        </Box>
        
        {/* Error and Success Messages */}
        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Category Dropdown */}
        <FormControl fullWidth>
          <InputLabel>Category *</InputLabel>
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            label="Category *"
            disabled={fetchingCategories || isReadOnly}
          >
            {categories.map((category) => (
              <MenuItem key={category.categoryId} value={category.categoryId}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* News Type Dropdown */}
        <FormControl fullWidth>
          <InputLabel>News Type *</InputLabel>
          <Select
            value={newsType}
            onChange={handleNewsTypeChange}
            label="News Type *"
            disabled={isReadOnly}
          >
            <MenuItem value={1}>National</MenuItem>
            <MenuItem value={2}>State</MenuItem>
            <MenuItem value={3}>District</MenuItem>
            <MenuItem value={4}>Constituency</MenuItem>
          </Select>
        </FormControl>

        {/* State Dropdown - Only show for State, District, and Constituency news */}
        {(newsType === 2 || newsType === 3 || newsType === 4) && (
          <FormControl fullWidth>
            <InputLabel>State *</InputLabel>
            <Select
              value={selectedState}
              onChange={handleStateChange}
              label="State *"
              disabled={fetchingStates || isReadOnly}
            >
              {states.map((state) => (
                <MenuItem key={state.stateId} value={state.stateId}>
                  {state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* District Dropdown - Only show for District and Constituency news */}
        {(newsType === 3 || newsType === 4) && (
          <FormControl fullWidth>
            <InputLabel>District *</InputLabel>
            <Select
              value={selectedDistrict}
              onChange={handleDistrictChange}
              label="District *"
              disabled={fetchingDistricts || !selectedState || isReadOnly}
            >
              {districts.map((district) => (
                <MenuItem key={district.districtId} value={district.districtId}>
                  {district.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Constituency Dropdown - Only show for Constituency news */}
        {newsType === 4 && (
          <FormControl fullWidth>
            <InputLabel>Constituency *</InputLabel>
            <Select
              value={selectedConstituency}
              onChange={(e) => setSelectedConstituency(e.target.value)}
              label="Constituency *"
              disabled={fetchingConstituencies || !selectedDistrict || isReadOnly}
            >
              {constituencies.map((constituency) => (
                <MenuItem key={constituency.constituencyId} value={constituency.constituencyId}>
                  {constituency.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Breaking News Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={isBreakingNews}
              onChange={(e) => setIsBreakingNews(e.target.checked)}
              disabled={isReadOnly}
              sx={{
                color: '#1976d2',
                '&.Mui-checked': {
                  color: '#1976d2',
                },
              }}
            />
          }
          label="Breaking News"
          sx={{ 
            color: '#1976d2', 
            fontWeight: 500,
            '& .MuiFormControlLabel-label': {
              fontWeight: 500
            }
          }}
        />

        {/* Send Push Notification Checkbox - Only for Super Admin and Approver roles */}
        {/* COMMENTED OUT: Previously restricted to Admin role (which included quality managers) */}
        {/* Now only Super Admin and Approver can send notifications, not Reporter or Quality Manager */}
        {/* Hide notification checkbox for Super Admin and Approver when post is published (status 4) */}
        {(userRole === 'Super Admin' || userRole === 'Approver') && articleData?.postStatus !== 4 && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendNotification}
                  onChange={(e) => {
                    setSendNotification(e.target.checked);
                    if (!e.target.checked) {
                      setNotificationTitle('');
                      // setNotificationBody(''); // Body field is hidden
                    }
                  }}
                  disabled={isReadOnly}
                  sx={{
                    color: '#1976d2',
                    '&.Mui-checked': {
                      color: '#1976d2',
                    },
                  }}
                />
              }
              label="Send Push Notification"
              sx={{ 
                color: '#1976d2', 
                fontWeight: 500,
                '& .MuiFormControlLabel-label': {
                  fontWeight: 500
                }
              }}
            />

            {/* Notification Title - Show only when checkbox is checked */}
            {/* Body parameter is hidden - only title is shown */}
            {sendNotification && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <TextField
                  label="Push Notification Title *"
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  fullWidth
                  required
                  disabled={isReadOnly}
                  placeholder="Enter notification title"
                />
                {/* Body field hidden - not required from user */}
                {/* <TextField
                  label="Push Notification Body *"
                  value={notificationBody}
                  onChange={(e) => setNotificationBody(e.target.value)}
                  fullWidth
                  required
                  multiline
                  rows={3}
                  disabled={isReadOnly}
                  placeholder="Enter notification body"
                /> */}
                <Button
                  variant="contained"
                  onClick={handleSendNotification}
                  disabled={submitting || !notificationTitle.trim() || isReadOnly}
                  startIcon={submitting ? null : <NotificationsActiveIcon />}
                  sx={{ 
                    bgcolor: '#FF6B35',
                    '&:hover': {
                      bgcolor: '#E85D2A'
                    }
                  }}
                >
                  {submitting ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                      Sending Notification...
                    </>
                  ) : (
                    'Send Push Notification'
                  )}
                </Button>
              </Box>
            )}
          </>
        )}

        {/* Title */}
        <TextField
          label="Title *"
          value={title}
          onChange={e => setTitle(capitalizeFirstLetter(e.target.value))}
          fullWidth
          required
          disabled={isReadOnly}
        />
        
        {/* Description */}
        <Box>
          <TextField
            label="Description *"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            required
            disabled={isReadOnly}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Word count: {description.trim() ? description.trim().split(/\s+/).filter(word => word.length > 0).length : 0}
          </Typography>
        </Box>

        {/* Media Upload Section */}
        <Box>
          <Typography variant="subtitle1" gutterBottom>Article Media</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current media type: {imageUrl ? 'Image' : videoUrl ? 'Video' : 'None'}
          </Typography>

          
          {!isReadOnly && (
            <>
              {/* Image Upload */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Image Upload</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Supported formats: JPG, PNG, GIF
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    component="label"
                    sx={{ width: 'fit-content' }}
                    disabled={!!video}
                  >
                    {imageUrl ? 'Change Image' : 'Select Image'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleImageChange}
                    />
                  </Button>
                  {imageUrl && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemoveImage}
                      sx={{ width: 'fit-content' }}
                    >
                      Remove Image
                    </Button>
                  )}
                </Box>
                {imageUrl && (
                  <Box sx={{ mb: 2 }}>
                    <img src={imageUrl} alt="Preview" style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8 }} />
                    <Typography variant="caption" color="info.main" display="block">
                      {image ? '✓ New image selected (will upload when you submit)' : '✓ Current article image'}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Video Upload */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>Video Upload</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Supported formats: MP4, AVI, MOV, WMV
                </Typography>
                <Typography variant="caption" color="warning.main" sx={{ mb: 1, display: 'block' }}>
                  ⚠️ Large video files may take longer to upload or fail. Consider compressing large videos.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    component="label"
                    sx={{ width: 'fit-content' }}
                    disabled={!!image}
                  >
                    {videoUrl ? 'Change Video' : 'Select Video'}
                    <input
                      type="file"
                      accept="video/*"
                      hidden
                      onChange={handleVideoChange}
                    />
                  </Button>
                  {videoUrl && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemoveVideo}
                      sx={{ width: 'fit-content' }}
                    >
                      Remove Video
                    </Button>
                  )}
                </Box>
                {videoUrl && (
                  <Box sx={{ mb: 2 }}>
                    <video 
                      src={videoUrl} 
                      controls 
                      style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8 }} 
                    />
                    <Typography variant="caption" color="info.main" display="block">
                      {video ? '✓ New video selected (will upload when you submit)' : '✓ Current article video'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* Read-only mode - show current media */}
          {isReadOnly && (
            <>
              {imageUrl && (
                <Box sx={{ mb: 2 }}>
                  <img src={imageUrl} alt="Article" style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8 }} />
                  <Typography variant="caption" color="info.main" display="block">
                    ✓ Article image
                  </Typography>
                </Box>
              )}
              {videoUrl && (
                <Box sx={{ mb: 2 }}>
                  <video 
                    src={videoUrl} 
                    controls 
                    style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8 }} 
                  />
                  <Typography variant="caption" color="info.main" display="block">
                    ✓ Article video
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>


        {/* Submit Button - Only show for non-read-only mode */}
        {!isReadOnly && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              submitting || 
              !title.trim() || 
              !description.trim() || 
              !selectedCategory ||
              (newsType === 2 && !selectedState) ||
              (newsType === 3 && (!selectedState || !selectedDistrict)) ||
              (newsType === 4 && (!selectedState || !selectedDistrict || !selectedConstituency))
            }
            sx={{ 
              mt: 2, 
              bgcolor: '#1976d2',
              '&:hover': {
                bgcolor: '#1565c0'
              }
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                {uploadingImage ? 'Uploading Image...' : uploadingVideo ? 'Uploading Video...' : 'Updating Article...'}
              </>
            ) : (
              'Update Article'
            )}
          </Button>
        )}
      </Paper>

      {/* Right: Mobile Preview and Action Buttons */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
        {/* Mobile Preview */}
        <Paper
          elevation={6}
          sx={{
            width: 320,
            height: 600,
            borderRadius: '32px',
            overflow: 'hidden',
            bgcolor: '#fafbfc',
            border: '1px solid #e0e0e0',
            p: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ bgcolor: '#1976d2', color: '#fff', p: 2, textAlign: 'center' }}>
            <Typography variant="subtitle2">Article Preview</Typography>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Media: Top 50% */}
            <Box sx={{ height: '50%', width: '100%', bgcolor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {imageUrl ? (
                <img src={imageUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : videoUrl ? (
                <video 
                  src={videoUrl} 
                  controls 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                />
              ) : (
                <Typography color="text.secondary">Media Preview</Typography>
              )}
            </Box>
            {/* Title: Next 10% */}
            <Box sx={{ height: '10%', width: '100%', display: 'flex', alignItems: 'center', px: 2, borderBottom: '1px solid #eee', borderTop: '1px solid #eee', bgcolor: '#fff' }}>
              <Typography variant="h6" fontWeight={700} sx={{ width: '100%', textAlign: 'center', fontSize: 18, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {title || <span style={{ color: '#bbb' }}>Title goes here</span>}
              </Typography>
            </Box>
            {/* Description: Bottom 40% */}
            <Box sx={{ height: '40%', width: '100%', p: 2, bgcolor: '#fafbfc', overflow: 'auto' }}>
              <Typography variant="body1" sx={{ color: '#444', fontSize: 15, wordBreak: 'break-word' }}>
                {description || <span style={{ color: '#bbb' }}>Description will appear here</span>}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Action Buttons - Only show for non-read-only mode */}
        {!isReadOnly && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            {articleData && articleData.postStatus === 1 && (
              <Button
                variant="contained"
                onClick={handleApprove}
                disabled={submitting}
                startIcon={<CheckCircleIcon />}
                sx={{ 
                  bgcolor: 'green',
                  '&:hover': {
                    bgcolor: 'darkgreen'
                  }
                }}
              >
                Approve
              </Button>
            )}
            {articleData && articleData.postStatus === 1 && (
              <Button
                variant="contained"
                onClick={handleReject}
                disabled={submitting}
                startIcon={<CancelIcon />}
                sx={{ 
                  bgcolor: 'red',
                  '&:hover': {
                    bgcolor: 'darkred'
                  }
                }}
              >
                Reject
              </Button>
            )}
            {articleData && articleData.postStatus === 2 && 
             userRole !== 'Admin' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleOpenPublishDialog}
                  disabled={submitting}
                  startIcon={<PublishIcon />}
                  sx={{ 
                    bgcolor: 'blue',
                    '&:hover': {
                      bgcolor: 'darkblue'
                    }
                  }}
                >
                  Publish
                </Button>
                {/* Schedule button - for both post type 1 and 2 */}
                <Button
                  variant="contained"
                  onClick={handleOpenScheduleDialog}
                  disabled={submitting}
                  startIcon={<ScheduleIcon />}
                  sx={{ 
                    bgcolor: 'green',
                    '&:hover': {
                      bgcolor: 'darkgreen'
                    }
                  }}
                >
                  Schedule
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Publish Dialog - Position Number Input */}
      <Dialog 
        open={publishDialogOpen} 
        onClose={(event, reason) => {
          // Prevent closing on backdrop click if position number is not valid
          if (reason === 'backdropClick' && (publishPositionNo === '' || publishPositionNo === undefined || publishPositionNo < 0 || publishPositionNo > 99)) {
            return;
          }
          handleClosePublishDialog();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Publish Post</DialogTitle>
        <DialogContent>
          <TextField
            key={publishDialogOpen ? 'publish-position-input' : 'publish-position-input-closed'}
            label="Position No *"
            type="number"
            fullWidth
            margin="normal"
            value={publishPositionNo === '' || publishPositionNo === undefined || publishPositionNo === null ? '' : String(publishPositionNo)}
            onChange={(e) => {
              const value = e.target.value;
              console.log('📝 Position number input changed:', value);
              if (value === '') {
                setPublishPositionNo('');
                return;
              }
              const numValue = parseInt(value);
              if (!isNaN(numValue) && numValue >= 0 && numValue <= 99) {
                setPublishPositionNo(numValue);
              } else {
                // Invalid value, keep as string to show error
                setPublishPositionNo(value);
              }
            }}
            inputProps={{ 
              min: 0,
              max: 99,
              step: 1
            }}
            placeholder="Enter position number (0-99)"
            required
            autoFocus
            error={publishPositionNo === '' || publishPositionNo === undefined || publishPositionNo === null || 
                   (typeof publishPositionNo === 'number' && (publishPositionNo < 0 || publishPositionNo > 99)) ||
                   (typeof publishPositionNo === 'string' && publishPositionNo !== '' && (isNaN(parseInt(publishPositionNo)) || parseInt(publishPositionNo) < 0 || parseInt(publishPositionNo) > 99))}
            helperText={
              publishPositionNo === '' || publishPositionNo === undefined || publishPositionNo === null 
                ? 'Position number is required (0-99)' 
                : (typeof publishPositionNo === 'number' && (publishPositionNo < 0 || publishPositionNo > 99) 
                  ? 'Position number must be between 0-99' 
                  : (typeof publishPositionNo === 'string' && publishPositionNo !== '' && (isNaN(parseInt(publishPositionNo)) || parseInt(publishPositionNo) < 0 || parseInt(publishPositionNo) > 99)
                    ? 'Please enter a valid number between 0-99'
                    : ''))
            }
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Enter the position number for publishing this post (0-99)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePublishDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePublish}
            disabled={
              submitting || 
              publishPositionNo === '' || 
              publishPositionNo === undefined || 
              publishPositionNo === null ||
              (typeof publishPositionNo === 'number' && (publishPositionNo < 0 || publishPositionNo > 99)) ||
              (typeof publishPositionNo === 'string' && (isNaN(parseInt(publishPositionNo)) || parseInt(publishPositionNo) < 0 || parseInt(publishPositionNo) > 99))
            }
            startIcon={submitting ? <CircularProgress size={20} /> : <PublishIcon />}
            sx={{ 
              bgcolor: 'blue',
              '&:hover': {
                bgcolor: 'darkblue'
              }
            }}
          >
            {submitting ? 'Publishing...' : 'Publish'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog - Position Number, Schedule Time, and Expiry Time (for post type 2) */}
      <Dialog 
        open={scheduleDialogOpen} 
        onClose={(event, reason) => {
          // Prevent closing on backdrop click if required fields are not valid
          if (reason === 'backdropClick' && (!scheduleTime || (postType === 2 && !expiryTime))) {
            return;
          }
          handleCloseScheduleDialog();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schedule Post</DialogTitle>
        <DialogContent>
          {/* Schedule Time */}
          <TextField
            label="Schedule Time *"
            type="datetime-local"
            fullWidth
            margin="normal"
            value={scheduleTime}
            onChange={(e) => setScheduleTime(e.target.value)}
            required
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: new Date().toISOString().slice(0, 16) // Prevent selecting past dates
            }}
            helperText="Select when to publish this post"
          />

          {/* Expiry Time - Only for post type 2 */}
          {postType === 2 && (
            <TextField
              label="Expiry Time *"
              type="datetime-local"
              fullWidth
              margin="normal"
              value={expiryTime}
              onChange={(e) => setExpiryTime(e.target.value)}
              required
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: scheduleTime || new Date().toISOString().slice(0, 16) // Must be after schedule time
              }}
              helperText="Select when this post should expire"
            />
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {postType === 1 
              ? 'Select when to schedule this post for publishing'
              : 'Select when to schedule this post and when it should expire'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseScheduleDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSchedule}
            disabled={
              submitting || 
              !scheduleTime ||
              (postType === 2 && !expiryTime)
            }
            startIcon={submitting ? <CircularProgress size={20} /> : <ScheduleIcon />}
            sx={{ 
              bgcolor: 'green',
              '&:hover': {
                bgcolor: 'darkgreen'
              }
            }}
          >
            {submitting ? 'Scheduling...' : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
